import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Spin, Card } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Endpoint de login desde configuración
  const LOGIN_URL = `${config.api.baseUrl}/login`;

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(LOGIN_URL, {
        username,
        password,
      });
      if (response.data.success && response.data.token) {
        // Extrae el rol del JWT si viene en el token, o del backend si viene directo
        let rol = response.data.rol;
        if (!rol && response.data.token) {
          try {
            const payload = JSON.parse(atob(response.data.token.split('.')[1]));
            rol = payload.rol;
          } catch (e) {
            rol = 'usuario';
          }
        }

        // Usar el context para login
        login(response.data.token, {
          username,
          nombre: response.data.nombre,
          apellido: response.data.apellido,
          rol: rol || 'usuario',
        });

        setError('');
        navigate('/');
      } else {
        setError(response.data.message || 'Usuario o contraseña incorrecta');
      }
    } catch (err: unknown) {
      setError('Usuario o contraseña incorrecta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(120deg, #1d39c4 50%, #52c41a 100%)', // geekblue-9 a green-7
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Card
          style={{
            width: 350,
            borderRadius: 12,
            boxShadow: '0 2px 8px #f0f1f2',
            textAlign: 'center',
            zIndex: 1,
            position: 'relative',
            margin: '0 auto',
          }}
        >
          <img
            src="/sq-logo.png"
            alt="Logo"
            style={{ width: 96, height: 96, objectFit: 'contain', marginBottom: 16 }}
          />
          <Typography.Title level={3} style={{ marginBottom: 12 }}>
            Iniciar Sesión
          </Typography.Title>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 8 }} />}
          <Form layout="vertical" onFinish={handleSubmit} style={{ marginTop: 12 }}>
            <Form.Item
              label="Usuario"
              name="username"
              rules={[{ required: true, message: 'Ingrese su usuario' }]}
            >
              <Input
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
              />
            </Form.Item>
            <Form.Item
              label="Contraseña"
              name="password"
              rules={[{ required: true, message: 'Ingrese su contraseña' }]}
            >
              <Input.Password
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                {loading ? 'Ingresando...' : 'Iniciar Sesión'}
              </Button>
            </Form.Item>
          </Form>
          {loading && <Spin style={{ marginTop: 12 }} />}
          {/* Botón para visitar la web de la empresa */}
          <Button
            type="default"
            shape="round"
            size="middle"
            icon={
              <GlobalOutlined
                style={{
                  fontSize: 18,
                  color: '#1d39c4',
                  marginRight: 4,
                  transition: 'transform 0.4s cubic-bezier(.68,-0.55,.27,1.55)',
                }}
              />
            }
            href="https://www.seprovinsa.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: '#fff',
              color: '#1d39c4',
              border: '1px solid #1d39c4',
              fontWeight: 500,
              marginTop: 18,
              marginBottom: 2,
              boxShadow: '0 1px 8px rgba(29,57,196,0.07)',
              transition: 'box-shadow 0.3s, transform 0.3s',
              width: 220,
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow =
                '0 4px 16px rgba(29,57,196,0.16)';
              (e.currentTarget as HTMLButtonElement).style.transform =
                'translateY(-2px) scale(1.04)';
              const icon = e.currentTarget.querySelector('span.anticon');
              if (icon) (icon as HTMLElement).style.transform = 'rotate(15deg) scale(1.15)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow =
                '0 1px 8px rgba(29,57,196,0.07)';
              (e.currentTarget as HTMLButtonElement).style.transform = 'none';
              const icon = e.currentTarget.querySelector('span.anticon');
              if (icon) (icon as HTMLElement).style.transform = 'none';
            }}
          >
            visitar seprovinsa.com
          </Button>
          {/* Footer de derechos dentro del Card */}
          <div
            style={{
              marginTop: 18,
              color: '#888',
              fontSize: 13,
              fontFamily: 'Roboto, Helvetica Neue, Arial, sans-serif',
              fontWeight: 400,
              letterSpacing: 0.1,
              textShadow: '0 1px 6px rgba(29,57,196,0.08)',
              lineHeight: 1.5,
              padding: '0 4px',
            }}
          >
            Derechos reservados Servicios Estratégicos de Protección y Vigilancia Integral S. A.
            (Seprovinsa Security).
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
